import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Input } from "@tabipla/ui";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@tabipla/ui";
import type { SpotResponseDto, NearbyMekkeSpotDto, NearbyFavoriteSpotDto } from "@tabipla/shared";
import { mapMekkeToTabittoCategory } from "@tabipla/shared";
import { PlaceAutocomplete, type PlaceResult } from "./PlaceAutocomplete";
import { DuplicateIndicator } from "./DuplicateWarning";
import { NearbySuggestions } from "./NearbySuggestions";
import { useDuplicateCheck, hasDuplicate } from "../../application/hooks/useDuplicateCheck";
import type { SpotCategory } from "@/domain/constants/categories";
import { SPOT_CATEGORIES } from "@/domain/constants/categories";

interface SpotFormData {
  name: string;
  description: string | null;
  category: SpotCategory;
  startTime: string | null;
  endTime: string | null;
  location: string | null;
  placeId: string | null;
  latitude: number | null;
  longitude: number | null;
  formattedAddress: string | null;
  cost: number | null;
  notes: string | null;
}

interface SpotFormProps {
  initialData?: SpotResponseDto;
  onSubmit: (data: SpotFormData) => void;
  onCancel: () => void;
  isSubmitting?: boolean;
  tripId?: string;
  destinationCoords?: { latitude: number; longitude: number } | null;
  defaultStartTime?: string | null;
}

export function SpotForm({
  initialData,
  onSubmit,
  onCancel,
  isSubmitting,
  tripId,
  destinationCoords,
  defaultStartTime,
}: SpotFormProps) {
  const { t } = useTranslation();

  const [formData, setFormData] = useState<SpotFormData>({
    name: initialData?.name ?? "",
    description: initialData?.description ?? null,
    category: (initialData?.category as SpotCategory) ?? "sightseeing",
    startTime: initialData?.startTime ?? defaultStartTime ?? null,
    endTime: initialData?.endTime ?? null,
    location: initialData?.location ?? null,
    placeId: initialData?.placeId ?? null,
    latitude: initialData?.latitude ?? null,
    longitude: initialData?.longitude ?? null,
    formattedAddress: initialData?.formattedAddress ?? null,
    cost: initialData?.cost ?? null,
    notes: initialData?.notes ?? null,
  });

  // Duplicate check
  const { data: duplicateData } = useDuplicateCheck({
    placeId: formData.placeId,
    tripId,
    enabled: !!formData.placeId && !initialData, // Only check for new spots
  });

  // Handler for selecting a nearby Mekke spot
  const handleSelectMekkeSpot = (spot: NearbyMekkeSpotDto) => {
    const category = mapMekkeToTabittoCategory(spot.category);
    setFormData({
      ...formData,
      name: spot.name,
      category,
      location: spot.name,
      placeId: spot.placeId ?? null,
      latitude: spot.latitude,
      longitude: spot.longitude,
      formattedAddress: spot.address ?? null,
    });
  };

  // Handler for selecting a nearby favorite spot
  const handleSelectFavoriteSpot = (spot: NearbyFavoriteSpotDto) => {
    const category = mapMekkeToTabittoCategory(spot.category);
    setFormData({
      ...formData,
      name: spot.name,
      category,
      location: spot.name,
      placeId: spot.placeId ?? null,
      latitude: spot.latitude,
      longitude: spot.longitude,
      formattedAddress: spot.formattedAddress ?? null,
    });
  };

  const handlePlaceChange = (place: PlaceResult | null) => {
    if (place) {
      setFormData({
        ...formData,
        location: place.name,
        placeId: place.placeId,
        latitude: place.latitude,
        longitude: place.longitude,
        formattedAddress: place.formattedAddress,
      });
    } else {
      setFormData({
        ...formData,
        location: null,
        placeId: null,
        latitude: null,
        longitude: null,
        formattedAddress: null,
      });
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formData);
  };

  // Use centralized category definitions
  const categories = SPOT_CATEGORIES;

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* Nearby suggestions from Mekke and Favorites */}
      {destinationCoords && !initialData && (
        <NearbySuggestions
          latitude={destinationCoords.latitude}
          longitude={destinationCoords.longitude}
          onSelectMekkeSpot={handleSelectMekkeSpot}
          onSelectFavoriteSpot={handleSelectFavoriteSpot}
          radiusKm={10}
        />
      )}

      {/* Name */}
      <div className="space-y-2">
        <label className="text-sm font-medium">{t("spots.name")}</label>
        <Input
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          placeholder={t("spots.namePlaceholder")}
          required
        />
      </div>

      {/* Category */}
      <div className="space-y-2">
        <label className="text-sm font-medium">{t("spots.category")}</label>
        <Select
          value={formData.category}
          onValueChange={(value) => setFormData({ ...formData, category: value as SpotCategory })}
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {categories.map((cat) => (
              <SelectItem key={cat} value={cat}>
                {t(`spots.categories.${cat}`)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Time */}
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <label className="text-sm font-medium">{t("spots.startTime")}</label>
          <Input
            type="time"
            value={formData.startTime ?? ""}
            onChange={(e) => setFormData({ ...formData, startTime: e.target.value || null })}
          />
        </div>
        <div className="space-y-2">
          <label className="text-sm font-medium">{t("spots.endTime")}</label>
          <Input
            type="time"
            value={formData.endTime ?? ""}
            onChange={(e) => setFormData({ ...formData, endTime: e.target.value || null })}
          />
        </div>
      </div>

      {/* Location */}
      <div className="space-y-2">
        <label className="text-sm font-medium">{t("spots.location")}</label>
        <PlaceAutocomplete
          value={formData.location ?? ""}
          onChange={handlePlaceChange}
          placeholder={t("spots.locationPlaceholder")}
        />
        {formData.formattedAddress && formData.formattedAddress !== formData.location && (
          <p className="text-xs text-muted-foreground">{formData.formattedAddress}</p>
        )}
        {/* Duplicate indicator */}
        {duplicateData && hasDuplicate(duplicateData) && (
          <DuplicateIndicator duplicateData={duplicateData} />
        )}
      </div>

      {/* Cost */}
      <div className="space-y-2">
        <label className="text-sm font-medium">{t("spots.cost")}</label>
        <Input
          type="number"
          value={formData.cost ?? ""}
          onChange={(e) =>
            setFormData({
              ...formData,
              cost: e.target.value ? Number(e.target.value) : null,
            })
          }
          placeholder={t("spots.costPlaceholder")}
          min={0}
        />
      </div>

      {/* Description */}
      <div className="space-y-2">
        <label className="text-sm font-medium">{t("spots.description")}</label>
        <textarea
          value={formData.description ?? ""}
          onChange={(e) => setFormData({ ...formData, description: e.target.value || null })}
          placeholder={t("spots.descriptionPlaceholder")}
          className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
        />
      </div>

      {/* Notes */}
      <div className="space-y-2">
        <label className="text-sm font-medium">{t("spots.notes")}</label>
        <Input
          value={formData.notes ?? ""}
          onChange={(e) => setFormData({ ...formData, notes: e.target.value || null })}
          placeholder={t("spots.notesPlaceholder")}
        />
      </div>

      {/* Actions */}
      <div className="flex justify-end gap-2 pt-4">
        <Button type="button" variant="outline" onClick={onCancel}>
          {t("common.cancel")}
        </Button>
        <Button type="submit" disabled={isSubmitting || !formData.name}>
          {isSubmitting
            ? initialData
              ? t("spots.updating")
              : t("spots.creating")
            : t("common.save")}
        </Button>
      </div>
    </form>
  );
}
